import { useState } from 'react';
import { Trash2, AlertCircle, Loader2 } from 'lucide-react';
import { deleteReorderPoint } from '../../../api/warehouse';
import type { ReorderPoint } from '../../../types/shared';

interface WarehouseReorderPointsTableProps {
  warehouseId: string;
  reorderPoints: ReorderPoint[];
  onRefresh: () => Promise<void>;
}

const WarehouseReorderPointsTable = ({
  warehouseId,
  reorderPoints,
  onRefresh,
}: WarehouseReorderPointsTableProps) => {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async (pointId: string) => {
    setDeleting(true);
    setError(null);
    try {
      await deleteReorderPoint(warehouseId, pointId);
      setDeleteConfirm(null);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete reorder point');
      setDeleting(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {reorderPoints.length === 0 ? (
        <p className="text-sm text-stone-600 dark:text-stone-400 py-4">
          No reorder points configured
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-[#242d28] border-b">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-bark-900 dark:text-stone-100">
                  Item ID
                </th>
                <th className="px-4 py-2 text-left font-medium text-bark-900 dark:text-stone-100">
                  Default Threshold
                </th>
                <th className="px-4 py-2 text-left font-medium text-bark-900 dark:text-stone-100">
                  Default Quantity
                </th>
                <th className="px-4 py-2 text-left font-medium text-bark-900 dark:text-stone-100">
                  Override Threshold
                </th>
                <th className="px-4 py-2 text-left font-medium text-bark-900 dark:text-stone-100">
                  Override Quantity
                </th>
                <th className="px-4 py-2 text-right font-medium text-bark-900 dark:text-stone-100">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {reorderPoints.map((point) => (
                <tr key={point.id} className="border-b hover:bg-stone-50 dark:hover:bg-[#242d28]">
                  <td className="px-4 py-2 text-stone-600 dark:text-stone-400">
                    {point.inventory_item_id.slice(0, 8)}...
                  </td>
                  <td className="px-4 py-2 text-bark-900 dark:text-stone-100">
                    {point.default_reorder_threshold}
                  </td>
                  <td className="px-4 py-2 text-bark-900 dark:text-stone-100">
                    {point.default_reorder_quantity}
                  </td>
                  <td className="px-4 py-2 text-stone-600 dark:text-stone-400">
                    {point.override_reorder_threshold ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-stone-600 dark:text-stone-400">
                    {point.override_reorder_quantity ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setDeleteConfirm(point.id)}
                      disabled={deleting}
                      className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded transition disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-bark-900 dark:text-stone-100 mb-2">
              Delete Reorder Point?
            </h3>
            <p className="text-stone-600 dark:text-stone-400 mb-6">
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting && <Loader2 size={14} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseReorderPointsTable;
